import React, { useEffect, useState } from "react";
import { EventBus } from "@engine/EventBus.js";

/**
 * S23 — small banner shown while a spell is armed and waiting for a target.
 *
 * Listens on `ui:spell_armed` / `ui:spell_disarmed`. Displays at the top
 * of the viewport (out of thumb range so it doesn't compete with the
 * tap target). Tells the player what to tap and how to cancel.
 */

interface ArmedState {
  readonly casterName: string;
  readonly spellName: string;
  readonly targeting: "single" | "aoe" | "multi" | "touch";
}

const HINT: Record<ArmedState["targeting"], string> = {
  single: "Tap an enemy",
  multi: "Tap targets, then ESC to confirm",
  aoe: "Tap a floor cell",
  touch: "Tap an ally",
};

export function SpellArmingToast(): React.ReactElement | null {
  const [armed, setArmed] = useState<ArmedState | null>(null);

  useEffect(() => {
    const offArm = EventBus.on("ui:spell_armed", (e) => {
      setArmed({
        casterName: e.casterName,
        spellName: e.spellName,
        targeting: e.targeting,
      });
    });
    const offDisarm = EventBus.on("ui:spell_disarmed", () => setArmed(null));
    return () => {
      offArm();
      offDisarm();
    };
  }, []);

  if (!armed) return null;

  return (
    <div style={toastStyle}>
      <span style={{ fontWeight: 600 }}>{armed.spellName}</span>
      <span style={{ opacity: 0.7, marginLeft: 8 }}>{HINT[armed.targeting]}</span>
      <span style={{ opacity: 0.5, marginLeft: 8, fontSize: 11 }}>(ESC to cancel)</span>
    </div>
  );
}

const toastStyle: React.CSSProperties = {
  position: "fixed",
  top: "calc(env(safe-area-inset-top, 0px) + 8px)",
  left: "50%",
  transform: "translateX(-50%)",
  background: "rgba(20, 30, 50, 0.92)",
  border: "1px solid rgba(155, 188, 214, 0.4)",
  color: "#eee",
  fontSize: 13,
  padding: "8px 14px",
  borderRadius: 4,
  pointerEvents: "none",
  userSelect: "none",
  zIndex: 150,
  whiteSpace: "nowrap",
  boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
};
